import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ModalProductCard from './ModalProductCard';
import FlavorImage from './FlavorImage';
import MobileProductVideo from './MobileProductVideo';
import { trackProductView, trackGetDirections } from '../utils/analytics';
import { getCanonicalProductSync } from '../utils/syncProductMatcher';
import getProductImagePath from '../utils/productImages';
import Cookies from 'universal-cookie';
import './StoreModal.scss';
import './StoreProductsLayout.scss';
import './ModalProductVideo.scss'; // Import dedicated modal video styles

/**
 * StoreModal component for displaying store details in a modal
 * This is used when clicking on a store logo/name in product cards
 */
const StoreModal = ({ store, onClose, product, products }) => {
  const modalRef = useRef(null);
  const canonicalProductCache = new Map();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // If no store, don't render
  if (!store) return null;

  // Handle get directions click with proper analytics tracking
const handleGetDirections = (e) => {
  e.preventDefault();
  
  // Debug output to see what's available in the store object
  if (process.env.NODE_ENV !== 'production') {
    console.log("DEBUG - Store object:", store);
    console.log("DEBUG - Store properties:", Object.keys(store));
    console.log("DEBUG - Store has fields property:", !!store.fields);
  }
  
  // IMPORTANT: Ensure store has a fields property with Lat/Lng like the other modal expects
  // If store doesn't have fields property already, create it with coordinates
  if (!store.fields) {
    store.fields = {
      Name: store.name,
      City: store.city,
      State: store.state,
      // Try to find coordinates from various properties
      Lat: store.lat || store.latitude || (store.location ? store.location.lat || store.location.latitude : undefined),
      Lng: store.lng || store.longitude || (store.location ? store.location.lng || store.location.longitude : undefined)
    };
    if (process.env.NODE_ENV !== 'production') {
      console.log("DEBUG - Created fields property with coordinates:", store.fields);
    }
  }
  
  // Detailed logging of all possible address-related properties
  if (process.env.NODE_ENV !== 'production') {
    console.log("DEBUG - Store address properties:", {
      name: store.name,
      address: store.address,
      street: store.street,
      city: store.city,
      state: store.state,
      zip: store.zip,
      zipCode: store.zipCode,
      postalCode: store.postalCode
    });
  
    if (store.fields) {
      console.log("DEBUG - Fields properties:", Object.keys(store.fields));
      console.log("DEBUG - Fields address properties:", {
        Name: store.fields.Name,
        Address: store.fields.Address,
        Street: store.fields.Street,
        City: store.fields.City,
        State: store.fields.State,
        ZipCode: store.fields.ZipCode,
        PostalCode: store.fields.PostalCode,
        ZIP: store.fields.ZIP
      });
    }
  }
  
  // Get user location from cookies
  const cookies = new Cookies();
  const userLocation = cookies.get('shopperLocation');
  
  // Get URL parameters for location
  const urlParams = new URLSearchParams(window.location.search);
  const urlLat = urlParams.get('lat');
  const urlLng = urlParams.get('lng');
  
  // DEBUG: Log the user location data from cookies
  if (process.env.NODE_ENV !== 'production') {
    console.log('DEBUG - User location from cookies:', userLocation);
    console.log('DEBUG - Store data:', store);
    console.log('DEBUG - URL location params:', { lat: urlLat, lng: urlLng });
  }
  
  // Track the event using analytics
  // Make sure we're passing the store object in the format expected by trackGetDirectionsGA4
  const storeForAnalytics = {
    fields: {
      Name: store.name || store.fields?.Name,
      City: store.city || store.fields?.City || '',
      State: store.state || store.fields?.State || '',
      Lat: store.lat || store.fields?.Lat,
      Lng: store.lng || store.fields?.Lng
    },
    id: store.id
  };
  trackGetDirections(storeForAnalytics);

  // Look for coordinates in various possible properties, with priority on fields.Lat/Lng like the other modal
  let destLat, destLng;
  
  // First check for the fields property structure that's used in the other modal
  if (store.fields && typeof store.fields.Lat !== 'undefined' && typeof store.fields.Lng !== 'undefined') {
    if (process.env.NODE_ENV !== 'production') {
      console.log('DEBUG - Found coordinates in fields property:', {
        fieldsLat: store.fields.Lat,
        fieldsLng: store.fields.Lng
      });
    }
    destLat = parseFloat(store.fields.Lat);
    destLng = parseFloat(store.fields.Lng);
  }
  // Fall back to alternate properties if fields isn't available
  else {
    // Try to find latitude in various properties
    if (typeof store.lat === 'number' || typeof store.lat === 'string') {
      destLat = typeof store.lat === 'number' ? store.lat : parseFloat(store.lat);
    } else if (typeof store.latitude === 'number' || typeof store.latitude === 'string') {
      destLat = typeof store.latitude === 'number' ? store.latitude : parseFloat(store.latitude);
    } else if (store.location && typeof store.location.lat === 'number') {
      destLat = store.location.lat;
    } else if (store.location && typeof store.location.latitude === 'number') {
      destLat = store.location.latitude;
    } else if (store.coordinates && store.coordinates.length >= 2) {
      destLat = store.coordinates[0]; // Assuming [lat, lng] format
    } else {
      // Attempt to get lat/lng from retailer name using Google Maps API or geocoding service
      // Note: This would require actual geocoding implementation or a service call
      if (process.env.NODE_ENV !== 'production') {
        console.log("DEBUG - No lat/lng found for store, would need geocoding:", store.name);
      }
      destLat = NaN;
    }
    
    // Try to find longitude in various properties
    if (typeof store.lng === 'number' || typeof store.lng === 'string') {
      destLng = typeof store.lng === 'number' ? store.lng : parseFloat(store.lng);
    } else if (typeof store.longitude === 'number' || typeof store.longitude === 'string') {
      destLng = typeof store.longitude === 'number' ? store.longitude : parseFloat(store.longitude);
    } else if (store.location && typeof store.location.lng === 'number') {
      destLng = store.location.lng;
    } else if (store.location && typeof store.location.longitude === 'number') {
      destLng = store.location.longitude;
    } else if (store.coordinates && store.coordinates.length >= 2) {
      destLng = store.coordinates[1]; // Assuming [lat, lng] format
    } else {
      destLng = NaN;
    }
    
    // If we STILL don't have valid coordinates, try to access the full store data
    // from the global context which might have more information
    if (isNaN(destLat) || isNaN(destLng)) {
      if (process.env.NODE_ENV !== 'production') {
        console.log("DEBUG - Trying to find store in global locations data");
      }
      
      // Try to find this store in the global pickupLocations array if it exists
      if (window.locationContext && window.locationContext.pickupLocations) {
        const fullStoreData = window.locationContext.pickupLocations.find(
          loc => loc.fields && loc.fields.Name === store.name
        );
        
        if (fullStoreData && fullStoreData.fields) {
          if (process.env.NODE_ENV !== 'production') {
            console.log("DEBUG - Found store in global data:", fullStoreData);
          }
          
          if (fullStoreData.fields.Lat && fullStoreData.fields.Lng) {
            destLat = parseFloat(fullStoreData.fields.Lat);
            destLng = parseFloat(fullStoreData.fields.Lng);
            
            // Update our store's fields property with this data
            if (store.fields) {
              store.fields.Lat = fullStoreData.fields.Lat;
              store.fields.Lng = fullStoreData.fields.Lng;
            }
            
            if (process.env.NODE_ENV !== 'production') {
              console.log("DEBUG - Retrieved coordinates from global data:", destLat, destLng);
            }
          }
        }
      }
    }
  }

  // Log all properties of the store object to help us identify where coordinates might be stored
  if (process.env.NODE_ENV !== 'production') {
    console.log('DEBUG - Store properties:', Object.keys(store));
  }
  
  // Check if we have a fields property which might contain the location data
  if (process.env.NODE_ENV !== 'production') {
    if (store.fields) {
      console.log('DEBUG - Store fields properties:', Object.keys(store.fields));
    }
  
    // Log raw values for debugging
    console.log('DEBUG - Raw lat/lng values:', { 
      rawLat: store.lat, 
      rawLng: store.lng,
      latitude: store.latitude,
      longitude: store.longitude,
      location: store.location,
      coordinates: store.coordinates,
      fieldsLat: store.fields?.Lat,
      fieldsLng: store.fields?.Lng,
      destLat, 
      destLng 
    });
  }

  // Validate coordinates are finite numbers and within valid ranges
  const isValidDestLat = Number.isFinite(destLat) && destLat >= -90 && destLat <= 90;
  const isValidDestLng = Number.isFinite(destLng) && destLng >= -180 && destLng <= 180;

  // DEBUG: Log destination coordinates
  if (process.env.NODE_ENV !== 'production') {
    console.log('DEBUG - Destination coordinates:', { 
      destLat, 
      destLng, 
      isValidDestLat, 
      isValidDestLng 
    });
  }

  if (isValidDestLat && isValidDestLng) {
    let mapsUrl;
    
    // First check if we have URL parameters for location
    const hasUrlLat = urlLat && urlLat.length > 0;
    const hasUrlLng = urlLng && urlLng.length > 0;
    const isUrlLatValid = hasUrlLat && Number.isFinite(parseFloat(urlLat));
    const isUrlLngValid = hasUrlLng && Number.isFinite(parseFloat(urlLng));
    
    // Then check if we have cookie location data
    const hasUserLat = userLocation && userLocation.lat;
    const hasUserLng = userLocation && userLocation.lng;
    const isUserLatValid = hasUserLat && Number.isFinite(parseFloat(userLocation.lat));
    const isUserLngValid = hasUserLng && Number.isFinite(parseFloat(userLocation.lng));
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('DEBUG - URL coordinates validity:', {
        hasUrlLat,
        hasUrlLng,
        isUrlLatValid,
        isUrlLngValid,
        urlLat: hasUrlLat ? parseFloat(urlLat) : null,
        urlLng: hasUrlLng ? parseFloat(urlLng) : null
      });
    
      console.log('DEBUG - User coordinates validity:', {
        hasUserLat,
        hasUserLng,
        isUserLatValid,
        isUserLngValid,
        userLat: hasUserLat ? parseFloat(userLocation.lat) : null,
        userLng: hasUserLng ? parseFloat(userLocation.lng) : null
      });
    }
    
    // IMPORTANT: Use exactly the same URL structure as the other modal (Locations.jsx line 1084)
    // This ensures the UI will be consistent with both "Get Directions" buttons
    
    // Create the simple destination URL exactly like the other modal - this is key!
    // The other modal just sets destination coordinates without origin parameters
    const destination = `${destLat},${destLng}`;
    mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    if (process.env.NODE_ENV !== 'production') {
      console.log('DEBUG - Using simple destination URL like other modal:', mapsUrl);
    
      console.log('DEBUG - Final maps URL:', mapsUrl);
    }
    
    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  } else {
    // Even if store coordinates are invalid, we can still use user's location
    // First check cookie data since it's now available based on logs
    const hasUserLat = userLocation && userLocation.lat;
    const hasUserLng = userLocation && userLocation.lng;
    const isUserLatValid = hasUserLat && Number.isFinite(parseFloat(userLocation.lat));
    const isUserLngValid = hasUserLng && Number.isFinite(parseFloat(userLocation.lng));
    
    // Then check URL parameters as a backup
    const hasUrlLat = urlLat && urlLat.length > 0;
    const hasUrlLng = urlLng && urlLng.length > 0;
    const isUrlLatValid = hasUrlLat && Number.isFinite(parseFloat(urlLat));
    const isUrlLngValid = hasUrlLng && Number.isFinite(parseFloat(urlLng));
    
    let searchUrl;
    
    // Format the destination address more precisely with proper address formatting
    // Include any address fields available
    let destinationAddress = '';
    
    // If we have fields data like the other modal uses, format the address the same way
    if (store.fields) {
      // Include complete address if available
      if (store.fields.Address) {
        destinationAddress = `${store.fields.Address}, `;
      }
      
      // Add full store info
      destinationAddress += `${store.fields.Name || store.name}`;
      
      // Add city and state if available
      if (store.fields.City || store.city) {
        destinationAddress += `, ${store.fields.City || store.city}`;
      }
      
      if (store.fields.State || store.state) {
        destinationAddress += `, ${store.fields.State || store.state}`;
      }
      
      // Add ZIP code if available
      if (store.fields.ZipCode) {
        destinationAddress += ` ${store.fields.ZipCode}`;
      }
    } else {
      // Use the regular store properties as fallback
      // Include street address if available
      if (store.address) {
        destinationAddress = `${store.address}, `;
      }
      
      // Add full store info
      destinationAddress += `${store.name}`;
      
      // Add city and state if available
      if (store.city) {
        destinationAddress += `, ${store.city}`;
      }
      
      if (store.state) {
        destinationAddress += `, ${store.state}`;
      }
      
      // Add ZIP code if available
      if (store.zipCode) {
        destinationAddress += ` ${store.zipCode}`;
      }
    }
    
    // Log the actual distance to verify it matches what's displayed
    if (process.env.NODE_ENV !== 'production') {
      console.log('DEBUG - Store distance from user:', {
        distance: store.distance,
        roundedDistance: Math.round(store.distance),
        unit: 'miles'
      });
      // Log the final destination address for debugging
      console.log('DEBUG - Final destination address:', destinationAddress);
    }
    
    // Enhance the destination address by adding "dispensary" if it's not already included
    // This helps Google Maps find the specific cannabis dispensary instead of a generic location
    if (!destinationAddress.toLowerCase().includes('dispensary')) {
      destinationAddress += ' dispensary';
    }
    
    // Try to match the exact format from the working example URL
    // This seems to improve the accuracy of the Google Maps search
    if (store.name && store.city && store.state) {
      const formattedName = store.name.replace(/&/g, 'and');
      destinationAddress = `${formattedName}, ${store.city}, ${store.state} dispensary`;
    }
    
    // IMPORTANT: Use exactly the same URL structure as the other modal (Locations.jsx line 1084)
    // The other modal doesn't include the origin parameter, so remove it here too
    // This is the key to getting Google Maps to label the starting point as "Your location"
    searchUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destinationAddress)}`;
    if (process.env.NODE_ENV !== 'production') {
      console.log('DEBUG - Using simple destination URL like other modal:', searchUrl);
    }
    
    window.open(searchUrl, '_blank', 'noopener,noreferrer');
  }
};  return (
    <div className="modal-overlay">
      <div className="modal-content" ref={modalRef}>
        <div className="modal-header">
          <h3>{store.name}</h3>
          <div className="modal-actions">
            {/* Always show the Get Directions button */}
            <button 
              onClick={handleGetDirections}
              className="directions-button"
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: '#0d6efd',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '600',
                boxShadow: 'none',
                cursor: 'pointer'
              }}
            >
              Get Directions
            </button>
            <button 
              onClick={onClose} 
              className="close-button"
              aria-label="Close modal"
            >
              &times;
            </button>
          </div>
        </div>
        
        <div className="modal-body">
          <div className="store-address">
            {store.city}, {store.state}
            {typeof store.distance === 'number' && isFinite(store.distance) && 
              ` (${store.distance.toFixed(1)} miles away)`}
          </div>
          
          {/* Product list section */}
          <div className="modal-product-list">
            {products && products.length > 0 ? (
              <div className="modal-products-wrapper">
                  {product && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-start', /* Instead of center */
                      margin: '0 auto 20px auto',
                      paddingBottom: '0',
                      width: '100%'
                    }}>
                      {(() => {
                        const config = product.configs?.[0];
                        const hasRecUrl = !!product.recreationalURL && product.recreationalURL.startsWith('http');
                        const hasMedUrl = !!product.medicalURL && product.medicalURL.startsWith('http');
                        const hasValidUrl = hasRecUrl || hasMedUrl;
                        const canonical = canonicalProductCache.get(product.name) || getCanonicalProductSync(product.name) || null;
                        const onSale = config && config.salePrice && config.salePrice < config.normalPrice;
                        const productUrl = hasRecUrl ? product.recreationalURL : product.medicalURL;
                        
                        // Instead of using the standard ModalProductCard, we'll create a custom layout
                        const imagePath = canonical ? getCanonicalProductSync(product.name)?.imagePath : null;
                        const productBrand = canonical?.brand || '';
                        const productDisplayName = canonical?.displayName || product.name;
                        const productTHC = canonical?.thc || '';
                        
                        let stockIndicator = 'in-stock';
                        if (config?.currentStock <= 0) stockIndicator = 'out-of-stock';
                        else if (config?.currentStock <= 5) stockIndicator = 'low-stock';
                        
                        // Menu type indicators
                        const isMedical = product.menuTypes?.includes('medical');
                        const isRecreational = product.menuTypes?.includes('recreational');
                        
                        return (
                          <div style={{
                            display: 'flex',
                            width: '100%',
                            maxWidth: isMobile ? '650px' : '460px',
                            margin: '0 auto',
                            padding: isMobile ? '15px' : '25px',
                            background: '#fff',
                            borderRadius: '18px',
                            boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
                            alignItems: 'center',
                            flexDirection: isMobile ? 'column' : 'row',
                            position: 'relative'
                          }}>
                            {/* Menu badge at the top right of the entire card for mobile */}
                            {isMobile && (isMedical || isRecreational) && (
                              <div style={{
                                position: 'absolute',
                                top: '18px',
                                right: '18px',
                                padding: '2px 12px',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: 700,
                                color: 'white',
                                zIndex: 5,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                background: isMedical 
                                  ? 'linear-gradient(90deg, #43a047 0%, #66bb6a 100%)' 
                                  : 'linear-gradient(90deg, #1976d2 0%, #2196f3 100%)'
                              }}>
                                {isMedical ? 'MED' : 'REC'}
                              </div>
                            )}
                            {/* Fixed media container with no class names */}
                            <div style={{
                              width: '160px',
                              height: '160px',
                              position: 'relative',
                              marginRight: isMobile ? '0' : '25px',
                              marginBottom: isMobile ? '15px' : '0',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'visible',
                              padding: 0,
                              boxSizing: 'content-box'
                            }}>
                              {/* Menu badge for desktop view only */}
                              {!isMobile && (isMedical || isRecreational) && (
                                <div style={{
                                  position: 'absolute',
                                  top: '-15px',
                                  left: '-5px',
                                  padding: '2px 12px',
                                  borderRadius: '8px',
                                  fontSize: '14px',
                                  fontWeight: 700,
                                  color: 'white',
                                  zIndex: 5,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                  background: isMedical 
                                    ? 'linear-gradient(90deg, #43a047 0%, #66bb6a 100%)' 
                                    : 'linear-gradient(90deg, #1976d2 0%, #2196f3 100%)'
                                }}>
                                  {isMedical ? 'MED' : 'REC'}
                                </div>
                              )}
                              
                              {/* Use the MobileProductVideo component with directVideo=true */}
                              <MobileProductVideo 
                                product={product}
                                canonicalProduct={canonical}
                                imagePath={imagePath}
                                inModal={true} 
                                directVideo={true}
                              />
                            </div>
                            <div style={{
                              flex: '1 1 auto',
                              padding: '0 10px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center',
                              width: isMobile ? '100%' : 'auto'
                            }}>
                              <h2 style={{
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                fontSize: isMobile ? '22px' : '28px',
                                fontWeight: '700',
                                color: '#1a1a1a',
                                marginTop: '0',
                                marginBottom: '8px',
                                lineHeight: '1.1'
                              }}>{productDisplayName}</h2>
                              {(productBrand || productTHC) && (
                                <div style={{
                                  fontSize: '16px',
                                  color: '#666',
                                  marginBottom: '16px'
                                }}>
                                  {productBrand && productTHC 
                                    ? `${productBrand} • ${productTHC}`
                                    : (productBrand || productTHC)
                                  }
                                </div>
                              )}
                              <div style={{
                                fontSize: isMobile ? '20px' : '24px',
                                fontWeight: '700',
                                marginBottom: '15px'
                              }}>
                                {onSale ? (
                                  <>
                                    <span style={{
                                      color: '#e53935', 
                                      marginRight: '10px'
                                    }}>${typeof config.salePrice === 'number' ? 
                                      (config.salePrice % 1 === 0 ? config.salePrice : config.salePrice.toFixed(2)) : 
                                      config.salePrice}</span>
                                    <span style={{
                                      color: '#888', 
                                      textDecoration: 'line-through', 
                                      fontSize: isMobile ? '16px' : '18px'
                                    }}>${typeof config.normalPrice === 'number' ? 
                                      (config.normalPrice % 1 === 0 ? config.normalPrice : config.normalPrice.toFixed(2)) : 
                                      config.normalPrice}</span>
                                  </>
                                ) : (
                                  <span style={{
                                    color: '#1a1a1a'
                                  }}>${typeof config?.normalPrice === 'number' ? 
                                    (config.normalPrice % 1 === 0 ? config.normalPrice : config.normalPrice.toFixed(2)) : 
                                    config?.normalPrice}</span>
                                )}
                              </div>
                              
                              <div className="product-meta" style={{ 
                                fontSize: isMobile ? '14px' : '16px',
                                flexWrap: isMobile ? 'wrap' : 'nowrap',
                                justifyContent: 'center',
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                <span className={`stock-info ${stockIndicator}`}> 
                                  {!config ? (
                                    "Stock unknown"
                                  ) : config.currentStock <= 0 ? (
                                    "Out of Stock"
                                  ) : config.currentStock <= 5 ? (
                                    `${config.currentStock} left`
                                  ) : (
                                    `${config.currentStock > 20 ? '20+' : config.currentStock} left`
                                  )}
                                </span>
                                <span className="separator" style={{ margin: isMobile ? '0 8px' : '0 12px' }}>|</span>
                                <span className="distance-info">
                                  {typeof store.distance === 'number' && isFinite(store.distance) ? (
                                    <>{store.distance.toFixed(2)} mi</>
                                  ) : (
                                    store.city && store.state ? <>{store.city}, {store.state}</> : ''
                                  )}
                                </span>
                              </div>
                              
                              {hasValidUrl ? (
                                <button
                                  className="view-details-button"
                                  onClick={() => {
                                    if (hasValidUrl && config?.currentStock > 0) {
                                      window.open(productUrl, '_blank', 'noopener,noreferrer');
                                      trackProductView(product, {
                                        fields: {
                                          Name: store.name,
                                          City: store.city || '',
                                          State: store.state || ''
                                        }
                                      }, canonical);
                                    }
                                  }}
                                  disabled={!hasValidUrl || config?.currentStock <= 0}
                                  style={{
                                    background: '#1976d2',
                                    color: '#fff',
                                    fontWeight: '700',
                                    padding: isMobile ? '8px 20px' : '10px 24px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    fontSize: isMobile ? '14px' : '15px',
                                    cursor: 'pointer',
                                    transition: 'background 0.2s',
                                    width: 'auto',
                                    marginTop: '10px',
                                    boxShadow: 'none'
                                  }}
                                >
                                  View details
                                </button>
                              ) : (
                                <button 
                                  className="view-details-button disabled" 
                                  disabled
                                  style={{
                                    background: '#eee',
                                    color: '#aaa',
                                    fontWeight: '700',
                                    padding: isMobile ? '8px 20px' : '10px 24px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    fontSize: isMobile ? '14px' : '15px',
                                    cursor: 'not-allowed',
                                    width: 'auto',
                                    marginTop: '10px',
                                    boxShadow: 'none'
                                  }}
                                >
                                  Link Unavailable
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Show other products with a separator */}
                  {products.length > 1 && (
                    <div className="other-products-section">
                      <div className="other-products-separator">
                        <h4>Other Products Available at {store.name}</h4>
                      </div>
                      <div className="other-products-container">
                        {products
                          .filter(p => !product || p.id !== product.id) // Filter out the current product
                        .map(otherProduct => {
                          const config = otherProduct.configs?.[0];
                          const hasRecUrl = !!otherProduct.recreationalURL && otherProduct.recreationalURL.startsWith('http');
                          const hasMedUrl = !!otherProduct.medicalURL && otherProduct.medicalURL.startsWith('http');
                          const hasValidUrl = hasRecUrl || hasMedUrl;
                          const canonical = canonicalProductCache.get(otherProduct.name) || getCanonicalProductSync(otherProduct.name) || null;
                          const onSale = config && config.salePrice && config.salePrice < config.normalPrice;
                          const productUrl = hasRecUrl ? otherProduct.recreationalURL : otherProduct.medicalURL;
                          
                          return (
                            <ModalProductCard
                              key={otherProduct.id}
                              product={otherProduct}
                              config={config}
                              canonicalProduct={canonical}
                              onSale={onSale}
                              hasValidUrl={hasValidUrl}
                              small={true}
                              distance={store.distance}
                              city={store.city}
                              state={store.state}
                              onViewDetails={() => {
                                if (hasValidUrl && config?.currentStock > 0) {
                                  window.open(productUrl, '_blank', 'noopener,noreferrer');
                                  trackProductView(otherProduct, {
                                    fields: {
                                      Name: store.name,
                                      City: store.city || '',
                                      State: store.state || ''
                                    }
                                  }, canonical);
                                }
                              }}
                            />
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="no-products-message">
                <p>No product information available for this store</p>
                {store.websiteUrl && (
                  <div className="store-modal-actions">
                    <a 
                      href={store.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="store-modal-button"
                    >
                      Visit Website
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="price-disclaimer">
            Prices shown are approximate and may not include taxes. Product availability and final pricing 
            may vary at time of purchase.
          </div>
        </div>
      </div>
    </div>
  );
};

StoreModal.propTypes = {
  store: PropTypes.shape({
    name: PropTypes.string.isRequired,
    city: PropTypes.string,
    state: PropTypes.string,
    address: PropTypes.string,
    phone: PropTypes.string,
    hours: PropTypes.string,
    imageUrl: PropTypes.string,
    websiteUrl: PropTypes.string,
    distance: PropTypes.number,
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  product: PropTypes.object, // Current product from card
  products: PropTypes.array, // All products from this store
};

export default StoreModal;
